// SimpleHubAuction.jsx - Sequential hub auction with early bird bonus
// Position determines bid ORDER - earlier positions bid blind but get multiplier bonus
// Later positions see previous bids but their bids count at face value

import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useWorldState } from "../state/WorldStateContext.jsx";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { soundManager, SOUNDS } from "../../systems/SoundManager";
import { GLOBAL_AIRPORTS } from "../../data/GlobalAirports.js";
import { debug } from '../../lib/debug.js';

// Hub data with base auction values
const HUB_VALUES = {
  // Mega Hubs
  KATL: { tier: "Mega Hub", baseValue: 65000000 },
  KJFK: { tier: "Mega Hub", baseValue: 70000000 },
  KLAX: { tier: "Mega Hub", baseValue: 68000000 },
  KORD: { tier: "Mega Hub", baseValue: 62000000 },
  KDFW: { tier: "Mega Hub", baseValue: 60000000 },
  // Major Hubs
  KDEN: { tier: "Major Hub", baseValue: 50000000 },
  KSFO: { tier: "Major Hub", baseValue: 55000000 },
  KSEA: { tier: "Major Hub", baseValue: 48000000 },
  KMIA: { tier: "Major Hub", baseValue: 52000000 },
  KPHX: { tier: "Major Hub", baseValue: 45000000 },
  KBOS: { tier: "Major Hub", baseValue: 50000000 },
  KEWR: { tier: "Major Hub", baseValue: 55000000 },
  KMSP: { tier: "Major Hub", baseValue: 42000000 },
  KDTW: { tier: "Major Hub", baseValue: 40000000 },
  KCLT: { tier: "Major Hub", baseValue: 45000000 },
  // Regional Hubs
  KSLC: { tier: "Regional Hub", baseValue: 35000000 },
  KLAS: { tier: "Regional Hub", baseValue: 38000000 },
  KSAN: { tier: "Regional Hub", baseValue: 32000000 },
  KPDX: { tier: "Regional Hub", baseValue: 30000000 },
  KBNA: { tier: "Regional Hub", baseValue: 28000000 },
  KAUS: { tier: "Regional Hub", baseValue: 30000000 },
  KRDU: { tier: "Regional Hub", baseValue: 26000000 },
  KCMH: { tier: "Regional Hub", baseValue: 24000000 },
  KPIT: { tier: "Regional Hub", baseValue: 25000000 },
  KSTL: { tier: "Regional Hub", baseValue: 26000000 },
};

const DEFAULT_HUB_VALUE = { tier: "Regional Hub", baseValue: 25000000 };

// Region definitions for AI bidding preferences
const REGIONS = {
  west_coast: ["KSEA", "KPDX", "KSFO", "KOAK", "KSJC", "KLAX", "KSAN", "KLAS"],
  east_coast: ["KBOS", "KJFK", "KEWR", "KPHL", "KBWI", "KDCA", "KCLT", "KATL", "KMIA"],
  mountain_west: ["KSLC", "KDEN", "KPHX", "KLAS", "KBOI", "KGEG"],
  southwest: ["KDFW", "KIAH", "KAUS", "KSAT", "KPHX", "KLAS", "KABQ"],
  southeast: ["KATL", "KCLT", "KRDU", "KRIC", "KGSO", "KMCO", "KTPA", "KJAX"],
  midwest: ["KORD", "KMDW", "KDTW", "KMSP", "KMKE", "KCLE", "KCVG", "KSTL"],
  northeast: ["KBOS", "KBDL", "KPVD", "KALB", "KBUF", "KSYR"],
};

// Helper to get hub region
const getHubRegion = (hubCode) => {
  for (const [region, hubs] of Object.entries(REGIONS)) {
    if (hubs.includes(hubCode)) return region;
  }
  return "other";
};

// 9 defunct AI airlines (+ player = 10 total, 8 selected per auction)
const AI_AIRLINES = [
  {
    id: "panam",
    name: "Pan American World Airways",
    shortName: "Pan Am",
    color: "#1e40af", // Pan Am blue
    budgetRange: { min: 80000000, max: 120000000 },
    preferredHubs: ["KJFK", "KLAX", "KMIA", "KSFO", "KORD"],
    hubWeights: { international: 1.5, major: 1.2, regional: 0.3 },
    bidStrategy: "overbid",
    regionPreference: "none",
  },
  {
    id: "twa",
    name: "Trans World Airlines",
    shortName: "TWA",
    color: "#dc2626", // TWA red
    budgetRange: { min: 50000000, max: 90000000 },
    preferredHubs: ["KSTL", "KJFK", "KLAX", "KORD", "KLAS"],
    hubWeights: { international: 1.1, major: 1.3, regional: 0.7 },
    bidStrategy: "wait_and_beat",
    regionPreference: "none",
  },
  {
    id: "eastern",
    name: "Eastern Air Lines",
    shortName: "Eastern",
    color: "#0891b2", // Eastern teal
    budgetRange: { min: 50000000, max: 80000000 },
    preferredHubs: ["KATL", "KMIA", "KBOS", "KPHL", "KRDU", "KCLT"],
    hubWeights: { international: 1.0, major: 1.4, regional: 1.0 },
    bidStrategy: "regional_aggressive",
    regionPreference: "east_coast",
  },
  {
    id: "braniff",
    name: "Braniff International Airways",
    shortName: "Braniff",
    color: "#f97316", // Braniff orange
    budgetRange: { min: 35000000, max: 85000000 },
    preferredHubs: ["KDFW", "KDEN", "KPHX", "KLAS", "KLAX", "KIAH"],
    hubWeights: { international: 1.1, major: 1.2, regional: 0.8 },
    bidStrategy: "random",
    regionPreference: "southwest",
  },
  {
    id: "psa",
    name: "Pacific Southwest Airlines",
    shortName: "PSA",
    color: "#e11d48", // PSA smile red
    budgetRange: { min: 40000000, max: 70000000 },
    preferredHubs: ["KSAN", "KLAX", "KSFO", "KSEA", "KPDX", "KLAS", "KOAK"],
    hubWeights: { international: 0.9, major: 1.5, regional: 1.2 },
    bidStrategy: "all_in_regional",
    regionPreference: "west_coast_only", // HARD LOCK - won't bid outside
  },
  {
    id: "western",
    name: "Western Airlines",
    shortName: "Western",
    color: "#ea580c", // Western orange-red
    budgetRange: { min: 35000000, max: 60000000 },
    preferredHubs: ["KSLC", "KDEN", "KSEA", "KPHX", "KSFO", "KLAS"],
    hubWeights: { international: 0.8, major: 1.0, regional: 1.1 },
    bidStrategy: "conservative",
    regionPreference: "mountain_west",
  },
  {
    id: "piedmont",
    name: "Piedmont Airlines",
    shortName: "Piedmont",
    color: "#4f46e5", // Piedmont blue
    budgetRange: { min: 30000000, max: 50000000 },
    preferredHubs: ["KCLT", "KRDU", "KATL", "KRIC", "KGSO", "KMCO"],
    hubWeights: { international: 0.6, major: 0.9, regional: 1.3 },
    bidStrategy: "early_dropout",
    regionPreference: "southeast",
  },
  {
    id: "continental",
    name: "Continental Airlines",
    shortName: "Continental",
    color: "#0369a1", // Continental blue
    budgetRange: { min: 60000000, max: 90000000 },
    preferredHubs: ["KIAH", "KEWR", "KCLE", "KDEN", "KORD", "KSFO"],
    hubWeights: { international: 1.3, major: 1.4, regional: 0.7 },
    bidStrategy: "strategic_premium",
    regionPreference: "none",
  },
  {
    id: "airtran",
    name: "AirTran Airways",
    shortName: "AirTran",
    color: "#65a30d", // AirTran green
    budgetRange: { min: 25000000, max: 45000000 },
    preferredHubs: ["KATL", "KMDW", "KBWI", "KMKE", "KMCO", "KFLL"],
    hubWeights: { international: 0.7, major: 1.0, regional: 1.2 },
    bidStrategy: "lowball",
    regionPreference: "none",
  },
];

// Position multipliers - earlier positions get bonus for bidding blind (10 total bidders)
const POSITION_MULTIPLIERS = {
  1: 1.45, // Bids completely blind, 45% bonus
  2: 1.40,
  3: 1.35,
  4: 1.30,
  5: 1.25,
  6: 1.20,
  7: 1.15,
  8: 1.10,
  9: 1.05,
  10: 1.00, // Sees all bids, no bonus
};

// Format currency
const formatMoney = (amount) => {
  if (amount >= 1000000000) return `$${(amount / 1000000000).toFixed(2)}B`;
  if (amount >= 1000000) return `$${(amount / 1000000).toFixed(1)}M`;
  if (amount >= 1000) return `$${(amount / 1000).toFixed(0)}K`;
  return `$${amount.toLocaleString()}`;
};

// Calculate perceived hub value for an AI airline
const calculatePerceivedValue = (airline, hubCode, hubInfo, isFirstAuction) => {
  const { baseValue, tier } = hubInfo;
  const hubRegion = getHubRegion(hubCode);

  // Check hard regional lock (PSA only bids west coast)
  if (airline.regionPreference?.includes("only")) {
    const allowedRegion = airline.regionPreference.replace("_only", "");
    if (hubRegion !== allowedRegion) {
      return 0; // Won't bid at all
    }
  }

  // Get tier multiplier from hub weights
  const tierKey = tier?.toLowerCase().includes("mega") ? "international" :
                  tier?.toLowerCase().includes("major") ? "major" : "regional";
  const tierMultiplier = airline.hubWeights?.[tierKey] || 1.0;

  let perceivedValue = baseValue * tierMultiplier;

  // Apply regional preference modifier
  if (airline.regionPreference && airline.regionPreference !== "none") {
    const cleanRegion = airline.regionPreference.replace("_only", "");
    const inPreferredRegion = hubRegion === cleanRegion ||
      // Also check if hub is in their preferred list
      airline.preferredHubs?.includes(hubCode);

    if (!inPreferredRegion) {
      perceivedValue *= 0.5; // Half value outside region
    }
  }

  // First auction: Reduce perceived value so player can win
  if (isFirstAuction) {
    perceivedValue *= 0.4;
  }

  return perceivedValue;
};

// Generate AI budget - uses airline's budgetRange property
// isFirstAuction: AI is less aggressive on player's first hub to avoid demoralizing new players
const generateAIBudget = (airline, baseValue, isFirstAuction = false) => {
  const { budgetRange } = airline;

  // First auction: AI budgets are significantly lower (player should win with reasonable bid)
  if (isFirstAuction) {
    const reducedMax = budgetRange.min * 0.5; // Only up to 50% of their min budget
    return Math.round((reducedMax * (0.5 + Math.random() * 0.5)) / 1000000) * 1000000;
  }

  // Normal auction: Random within their budget range
  const budget = budgetRange.min + Math.random() * (budgetRange.max - budgetRange.min);

  // Round to nearest $1M
  return Math.round(budget / 1000000) * 1000000;
};

// Generate AI bid - uses strategy-based bidding per airline personality
const generateAIBid = (airline, hubCode, hubInfo, position, previousBids = [], budget, perceivedValue, isFirstAuction) => {
  const multiplier = POSITION_MULTIPLIERS[position];
  const { bidStrategy } = airline;

  // If perceived value is 0, airline won't bid (regional lock)
  if (perceivedValue === 0) {
    debug(`[AI] ${airline.shortName} PASSES - won't bid outside their region`);
    return 0;
  }

  // Get current highest effective bid
  const highestPreviousBid = previousBids.length > 0
    ? Math.max(...previousBids.map(b => b.effectiveBid))
    : 0;

  // Calculate what we'd need to bid to win (accounting for our position multiplier)
  const neededToWin = highestPreviousBid > 0
    ? Math.ceil(highestPreviousBid / multiplier) + 1000000
    : Math.round(perceivedValue * 0.5);

  // CRITICAL: If needed bid exceeds our budget, PASS
  if (neededToWin > budget) {
    debug(`[AI] ${airline.shortName} PASSES - needed $${(neededToWin/1000000).toFixed(1)}M exceeds budget $${(budget/1000000).toFixed(1)}M`);
    return 0;
  }

  let bid = 0;

  switch (bidStrategy) {
    case "overbid": // Pan Am - deep pockets, prestige focused
      // Bids 15-25% above perceived value
      bid = perceivedValue * (1.15 + Math.random() * 0.10);
      break;

    case "wait_and_beat": // TWA - strategic calculator
      if (previousBids.length > 0 && highestPreviousBid > 0) {
        // Beat highest by 5-10%
        bid = (highestPreviousBid / multiplier) * (1.05 + Math.random() * 0.05);
      } else {
        // No info, bid at perceived value
        bid = perceivedValue;
      }
      break;

    case "regional_aggressive": // Eastern - aggressive on their turf
      const hubRegion = getHubRegion(hubCode);
      const inRegion = hubRegion === airline.regionPreference || airline.preferredHubs?.includes(hubCode);
      if (inRegion) {
        // 20-30% premium on their turf
        bid = perceivedValue * (1.20 + Math.random() * 0.10);
      } else {
        // 20-30% under outside region
        bid = perceivedValue * (0.70 + Math.random() * 0.10);
      }
      break;

    case "random": // Braniff - unpredictable
      if (Math.random() > 0.5) {
        // Aggressive mode
        bid = perceivedValue * (1.15 + Math.random() * 0.10);
      } else {
        // Conservative mode
        bid = perceivedValue * (0.80 + Math.random() * 0.10);
      }
      break;

    case "all_in_regional": // PSA - west coast loyalist
      // Goes 25-40% above value (already filtered by perceivedValue check)
      bid = perceivedValue * (1.25 + Math.random() * 0.15);
      break;

    case "conservative": // Western - patient bargain hunter
      // Bids 10-20% below value
      bid = perceivedValue * (0.80 + Math.random() * 0.10);
      // Drop out if bidding gets high
      if (highestPreviousBid > perceivedValue) {
        debug(`[AI] ${airline.shortName} PASSES - price too high for a bargain hunter`);
        return 0;
      }
      break;

    case "early_dropout": // Piedmont - risk averse
      // Bids at perceived value
      bid = perceivedValue;
      // Drop out if anyone bid 10%+ above value
      if (highestPreviousBid > perceivedValue * 1.1) {
        debug(`[AI] ${airline.shortName} PASSES - price escalated too much`);
        return 0;
      }
      break;

    case "strategic_premium": // Continental - hub fortress builder
      // Check if hub is in their preferred list (strategic synergy)
      const hasStrategicValue = airline.preferredHubs?.includes(hubCode);
      const synergy = hasStrategicValue ? 0.3 : 0.1; // Up to 30% premium for strategic hubs
      bid = perceivedValue * (1.0 + synergy);
      break;

    case "lowball": // AirTran - budget focused
      // Always bids 30-40% below value
      bid = perceivedValue * (0.60 + Math.random() * 0.10);
      // Walk away if anyone bids above value
      if (highestPreviousBid > perceivedValue) {
        debug(`[AI] ${airline.shortName} PASSES - only looks for steals`);
        return 0;
      }
      break;

    default:
      bid = perceivedValue;
  }

  // Clamp to budget
  bid = Math.min(bid, budget);

  // Round to nearest $100K
  return Math.round(bid / 100000) * 100000;
};

// Position Card Component
function PositionCard({ position, isRevealed, isPlayerCard, onClick, disabled }) {
  const [isHovered, setIsHovered] = useState(false);
  const multiplier = POSITION_MULTIPLIERS[position];

  return (
    <button
      onClick={onClick}
      disabled={disabled || isRevealed}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="relative transition-all duration-300"
      style={{
        width: "80px",
        height: "110px",
        perspective: "1000px",
        cursor: disabled || isRevealed ? "default" : "pointer",
      }}
    >
      <div
        className="absolute inset-0 rounded-lg transition-transform duration-500"
        style={{
          transformStyle: "preserve-3d",
          transform: isRevealed ? "rotateY(180deg)" : "rotateY(0deg)",
        }}
      >
        {/* Card Back */}
        <div
          className="absolute inset-0 rounded-lg flex items-center justify-center"
          style={{
            backfaceVisibility: "hidden",
            background: disabled && !isRevealed
              ? "linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)"
              : isHovered && !disabled
              ? "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)"
              : "linear-gradient(135deg, #374151 0%, #1f2937 100%)",
            border: isHovered && !disabled ? "2px solid #3b82f6" : "2px solid #4b5563",
            boxShadow: isHovered && !disabled ? "0 0 20px rgba(59, 130, 246, 0.5)" : "none",
          }}
        >
          <span className="text-3xl font-bold" style={{ color: disabled ? "#4b5563" : "#9ca3af" }}>
            ?
          </span>
        </div>

        {/* Card Front */}
        <div
          className="absolute inset-0 rounded-lg flex flex-col items-center justify-center"
          style={{
            backfaceVisibility: "hidden",
            transform: "rotateY(180deg)",
            background: isPlayerCard
              ? "linear-gradient(135deg, #166534 0%, #14532d 100%)"
              : "linear-gradient(135deg, #1e3a5f 0%, #172554 100%)",
            border: isPlayerCard ? "2px solid #22c55e" : "2px solid #3b82f6",
          }}
        >
          <span
            className="text-3xl font-bold"
            style={{ color: isPlayerCard ? "#22c55e" : "#60a5fa" }}
          >
            {position}
          </span>
          <span
            className="text-xs mt-1"
            style={{ color: isPlayerCard ? "#86efac" : "#93c5fd" }}
          >
            {position === 1 ? "1st" : position === 2 ? "2nd" : position === 3 ? "3rd" : `${position}th`}
          </span>
          <span
            className="text-xs mt-1 font-bold"
            style={{ color: "#fbbf24" }}
          >
            {multiplier}x
          </span>
        </div>
      </div>
    </button>
  );
}

// Bid Row for sequential display
function BidRow({ participant, isPlayer, isCurrentHighest, showBid, isWaiting }) {
  const multiplier = POSITION_MULTIPLIERS[participant.position];

  return (
    <div
      className="flex items-center justify-between p-3 rounded-lg transition-all duration-300"
      style={{
        backgroundColor: isWaiting
          ? "#1c1302"
          : isCurrentHighest && showBid
          ? "#14532d"
          : isPlayer
          ? "#1e3a5f"
          : "#1a1a1a",
        border: isWaiting
          ? "2px solid #f59e0b"
          : isCurrentHighest && showBid
          ? "2px solid #22c55e"
          : isPlayer
          ? "1px solid #3b82f6"
          : "1px solid #333",
        opacity: showBid || isWaiting ? 1 : 0.6,
      }}
    >
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-mono px-2 py-1 rounded" style={{ backgroundColor: "#333", color: "#9ca3af" }}>
            #{participant.position}
          </span>
          <span className="text-xs px-1 rounded" style={{ backgroundColor: "#f59e0b20", color: "#fbbf24" }}>
            {multiplier}x
          </span>
        </div>
        <div
          className="w-3 h-3 rounded-full"
          style={{ backgroundColor: participant.color }}
        />
        <span
          className="font-medium"
          style={{ color: isPlayer ? "#60a5fa" : "#e5e5e5" }}
        >
          {participant.name}
          {isPlayer && <Badge className="ml-2 text-xs" style={{ backgroundColor: "#3b82f6" }}>YOU</Badge>}
        </span>
      </div>
      <div className="flex items-center gap-3">
        {isWaiting ? (
          <span className="text-sm animate-pulse" style={{ color: "#f59e0b" }}>Bidding...</span>
        ) : showBid ? (
          <>
            <div className="text-right">
              <div className="font-bold" style={{ color: participant.bid === 0 ? "#6b7280" : "#e5e5e5" }}>
                {participant.bid === 0 ? "PASS" : formatMoney(participant.bid)}
              </div>
              {participant.bid > 0 && (
                <div className="text-xs" style={{ color: "#22c55e" }}>
                  = {formatMoney(participant.effectiveBid)} effective
                </div>
              )}
            </div>
            {isCurrentHighest && participant.bid > 0 && (
              <span style={{ color: "#fbbf24" }}>👑</span>
            )}
          </>
        ) : (
          <span style={{ color: "#4b5563" }}>—</span>
        )}
      </div>
    </div>
  );
}

// Helper to get hub info
const getHubInfo = (hubCode) => {
  const hubValue = HUB_VALUES[hubCode] || DEFAULT_HUB_VALUE;
  const airport = GLOBAL_AIRPORTS[hubCode] || { name: hubCode, city: "Unknown" };
  return {
    code: hubCode,
    name: airport.name || hubCode,
    city: airport.city || "",
    tier: hubValue.tier,
    baseValue: hubValue.baseValue,
  };
};

// Main Component
export default function SimpleHubAuction({ hub, onClose, onComplete }) {
  const { worldState, updateWorldState } = useWorldState();
  const playerCash = worldState?.airline?.cash || 0;
  const playerAirlineName = worldState?.airline?.name || "Your Airline";

  // Detect if this is player's first hub auction (only have starting hub, never won)
  const completedAuctions = worldState?.airline?.completedHubAuctions || [];
  const existingHubs = worldState?.airline?.hubs || ["KPDX"];
  const isFirstAuction = existingHubs.length === 1 && completedAuctions.length === 0;

  const hubInfo = useMemo(() => getHubInfo(hub), [hub]);

  // Auction state
  const [phase, setPhase] = useState("intro"); // intro -> draw -> bidding -> result
  const [playerPosition, setPlayerPosition] = useState(null);
  const [participants, setParticipants] = useState([]); // All 10 participants sorted by position
  const [cardsRevealed, setCardsRevealed] = useState([]);
  const [currentBidder, setCurrentBidder] = useState(0); // Index of who's currently bidding (1-10)
  const [playerBid, setPlayerBid] = useState("");
  const [playerHasBid, setPlayerHasBid] = useState(false);
  const [allBidsComplete, setAllBidsComplete] = useState(false);
  const [winner, setWinner] = useState(null);
  const [bidError, setBidError] = useState("");

  // Handle card pick
  const handleCardPick = useCallback((cardIndex) => {
    if (playerPosition !== null) return;

    const positions = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]; // 10 total bidders
    const shuffled = [...positions].sort(() => Math.random() - 0.5);
    const playerPos = shuffled[cardIndex];
    setPlayerPosition(playerPos);

    // All 9 AI airlines participate (+ player = 10 total)
    const shuffledAirlines = [...AI_AIRLINES].sort(() => Math.random() - 0.5);

    // Assign positions and FIXED BUDGETS to all participants
    // First auction: AI budgets are lower so player can win with reasonable bid
    const remainingPositions = shuffled.filter((_, i) => i !== cardIndex);
    const allParticipants = [
      ...shuffledAirlines.map((airline, index) => {
        // Calculate perceived value based on airline personality and hub
        const perceivedValue = calculatePerceivedValue(airline, hub, hubInfo, isFirstAuction);
        // Generate fixed budget for this AI based on their budget range
        const budget = generateAIBudget(airline, hubInfo.baseValue, isFirstAuction);
        debug(`[AI Budget] ${airline.shortName} (${airline.bidStrategy}): $${(budget/1000000).toFixed(1)}M, perceived value: $${(perceivedValue/1000000).toFixed(1)}M${isFirstAuction ? ' (FIRST AUCTION)' : ''}`);
        return {
          ...airline,
          position: remainingPositions[index],
          isPlayer: false,
          bid: null,
          effectiveBid: null,
          budget, // Fixed budget for this auction
          perceivedValue, // Pre-calculated perceived value
        };
      }),
      {
        id: "player",
        name: playerAirlineName,
        position: playerPos,
        isPlayer: true,
        color: "#22c55e",
        bid: null,
        effectiveBid: null,
        budget: Infinity, // Player has no AI budget constraint
        perceivedValue: hubInfo.baseValue,
      }
    ].sort((a, b) => a.position - b.position);

    setParticipants(allParticipants);
    setCardsRevealed([cardIndex]);

    // Reveal other cards (10 total cards)
    const otherIndices = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9].filter(i => i !== cardIndex);
    otherIndices.forEach((idx, i) => {
      setTimeout(() => {
        setCardsRevealed(prev => [...prev, idx]);
      }, 200 + i * 100); // Slightly faster for more cards
    });

    // Move to bidding phase
    setTimeout(() => {
      setPhase("bidding");
      setCurrentBidder(1); // Start with position 1
    }, 200 + 9 * 100 + 800);
  }, [playerPosition, playerAirlineName, hub, hubInfo, isFirstAuction]);

  // Process AI bid - use functional setState to avoid stale closures
  const processAIBid = useCallback((participantIndex) => {
    setParticipants(prev => {
      const participant = prev[participantIndex];
      if (!participant || participant.isPlayer) return prev;

      // Get previous bids from current state
      const previousBids = prev
        .filter(p => p.bid !== null)
        .map(p => ({ bid: p.bid, effectiveBid: p.effectiveBid }));

      const bid = generateAIBid(
        participant,           // Full airline object
        hub,                   // Hub code
        hubInfo,               // Hub info
        participant.position,
        previousBids,
        participant.budget,    // Fixed budget
        participant.perceivedValue, // Pre-calculated perceived value
        isFirstAuction
      );
      const multiplier = POSITION_MULTIPLIERS[participant.position];
      const effectiveBid = Math.round(bid * multiplier);

      return prev.map((p, i) =>
        i === participantIndex ? { ...p, bid, effectiveBid } : p
      );
    });
  }, [hub, hubInfo, isFirstAuction]);  // Removed participants dependency!

  // Ref to hold latest processAIBid without causing effect re-runs
  const processAIBidRef = useRef(processAIBid);
  useEffect(() => { processAIBidRef.current = processAIBid; }, [processAIBid]);

  // Handle bidding progression - use refs to avoid dependency issues
  // Store participants in ref for use in timer callbacks
  const participantsRef = useRef(participants);
  useEffect(() => { participantsRef.current = participants; }, [participants]);

  useEffect(() => {
    if (phase !== "bidding" || currentBidder === 0 || currentBidder > 10) return;

    const currentParticipant = participants.find(p => p.position === currentBidder);
    if (!currentParticipant) return;

    // If it's AI's turn, process their bid after a delay
    if (!currentParticipant.isPlayer && currentParticipant.bid === null) {
      const bidderPosition = currentBidder; // Capture current value
      const timer = setTimeout(() => {
        // Find index using ref to get latest participants
        const idx = participantsRef.current.findIndex(p => p.position === bidderPosition);
        if (idx !== -1) {
          processAIBidRef.current(idx);
        }

        // Move to next bidder using functional update
        setTimeout(() => {
          setCurrentBidder(prev => {
            if (prev < 10) {
              return prev + 1;
            } else {
              setAllBidsComplete(true);
              return prev;
            }
          });
        }, 600);
      }, 800 + Math.random() * 400);

      return () => clearTimeout(timer);
    }
  }, [phase, currentBidder, participants]);  // participants needed to detect bid changes

  // Handle player bid submission
  const handlePlayerBid = useCallback(() => {
    const bidAmount = parseInt(playerBid.replace(/,/g, "")) || 0;

    if (bidAmount > playerCash) {
      setBidError("You don't have enough cash!");
      return;
    }

    const multiplier = POSITION_MULTIPLIERS[playerPosition];
    const effectiveBid = Math.round(bidAmount * multiplier);

    setParticipants(prev => prev.map(p =>
      p.isPlayer ? { ...p, bid: bidAmount, effectiveBid } : p
    ));
    setPlayerHasBid(true);

    // Move to next bidder
    setTimeout(() => {
      if (currentBidder < 10) {
        setCurrentBidder(currentBidder + 1);
      } else {
        setAllBidsComplete(true);
      }
    }, 500);
  }, [playerBid, playerCash, playerPosition, currentBidder]);

  // Determine winner when all bids complete
  useEffect(() => {
    if (!allBidsComplete || winner) return;

    const validBids = participants.filter(p => p.bid > 0);
    if (validBids.length === 0) {
      // Everyone passed - no winner
      setWinner({ id: "none", name: "No one", bid: 0 });
    } else {
      const highestEffective = Math.max(...validBids.map(p => p.effectiveBid));
      const winnerParticipant = validBids.find(p => p.effectiveBid === highestEffective);
      setWinner(winnerParticipant);
    }

    setTimeout(() => setPhase("result"), 1500);
  }, [allBidsComplete, participants, winner]);

  // Handle auction completion
  const handleComplete = useCallback(() => {
    if (winner?.isPlayer) {
      soundManager.play(SOUNDS.HUB_WON);
      const bidAmount = participants.find(p => p.isPlayer)?.bid || 0;
      const currentHubs = worldState?.airline?.hubs || ["KPDX"];

      updateWorldState({
        airline: {
          ...worldState?.airline,
          cash: playerCash - bidAmount,
          hubs: [...currentHubs, hub],
          completedHubAuctions: [...(worldState?.airline?.completedHubAuctions || []), hub],
        },
        // Also update ownedHubs (used by WingsSystem, NewRouteModal, etc.)
        ownedHubs: [...new Set([...(worldState?.ownedHubs || ['KPDX']), hub])],
      });
    } else {
      // Player lost the auction
      const gameTime = worldState?.gameTime ? new Date(worldState.gameTime).getTime() : Date.now();
      const currentDay = worldState?.dayNumber || 1;
      const firstHubAttempts = worldState?.firstHubAttempts || 0;
      const completedAuctions = worldState?.airline?.completedHubAuctions || [];

      // Check if this was a first-hub attempt (only have starting hub, never won)
      const existingHubs = worldState?.airline?.hubs || ["KPDX"];
      const isFirstHubAttempt = existingHubs.length === 1 && completedAuctions.length === 0;

      const updates = {
        airline: {
          ...worldState?.airline,
          failedHubAuctions: [...(worldState?.airline?.failedHubAuctions || []), {
            hub,
            timestamp: Date.now(), // Keep real time for backwards compat
            gameTimestamp: gameTime, // Add game time for proper cooldown
          }],
        }
      };

      // If first-hub attempt failed, set the retry timer
      if (isFirstHubAttempt && firstHubAttempts <= 1) {
        updates.firstHubFailedDay = currentDay;
      }

      updateWorldState(updates);
    }

    onComplete?.(winner?.isPlayer ? hub : null, winner);
    onClose?.();
  }, [winner, participants, hub, playerCash, worldState, updateWorldState, onComplete, onClose]);

  // Get current highest bid for display
  const currentHighestBid = participants
    .filter(p => p.effectiveBid !== null && p.effectiveBid > 0)
    .reduce((max, p) => p.effectiveBid > max ? p.effectiveBid : max, 0);

  const currentHighestBidder = participants.find(p => p.effectiveBid === currentHighestBid);

  // Render intro
  const renderIntro = () => (
    <div className="space-y-6">
      <div className="text-center">
        <div
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4"
          style={{ backgroundColor: "#14532d", border: "1px solid #22c55e" }}
        >
          <span style={{ color: "#86efac" }}>Hub Invitation Earned!</span>
        </div>
        <h2 className="text-3xl font-bold mb-2" style={{ color: "#fbbf24" }}>
          {hubInfo.name}
        </h2>
        <p className="text-lg" style={{ color: "#9ca3af" }}>{hubInfo.city}</p>
        <Badge className="mt-2" style={{ backgroundColor: "#1e40af", color: "#93c5fd" }}>
          {hubInfo.tier}
        </Badge>
      </div>

      <div className="p-6 rounded-xl text-center" style={{ backgroundColor: "#0c1929", border: "1px solid #3b82f640" }}>
        <p className="text-lg mb-4" style={{ color: "#e5e5e5" }}>
          Your consistent service to <span className="font-bold" style={{ color: "#60a5fa" }}>{hub}</span> has earned you an invitation to bid on hub rights.
        </p>
      </div>

      <div className="p-4 rounded-lg" style={{ backgroundColor: "#1a1a1a", border: "1px solid #333" }}>
        <h3 className="font-bold mb-3" style={{ color: "#fbbf24" }}>How This Auction Works:</h3>
        <ul className="space-y-2 text-sm" style={{ color: "#a1a1a1" }}>
          <li>• You'll draw a position card (1-10) determining your bid order</li>
          <li>• Bids happen sequentially - you see all previous bids before yours</li>
          <li>• <span style={{ color: "#22c55e" }}>Early positions get bonus multipliers</span> (Position 1 = 1.45x, Position 10 = 1.0x)</li>
          <li>• Highest <span style={{ color: "#fbbf24" }}>effective bid</span> (your bid × multiplier) wins</li>
        </ul>
      </div>

      <div className="flex justify-center gap-4">
        <Button
          onClick={onClose}
          className="px-6 py-3"
          style={{ backgroundColor: "#1f2937", color: "#9ca3af", border: "1px solid #374151" }}
        >
          Maybe Later
        </Button>
        <Button
          onClick={() => setPhase("draw")}
          className="px-8 py-3 text-lg font-bold"
          style={{ backgroundColor: "#166534", color: "#fff" }}
        >
          Enter Auction
        </Button>
      </div>
    </div>
  );

  // Render draw phase
  const renderDraw = () => (
    <div className="space-y-6">
      <div className="text-center">
        <Badge className="mb-2" style={{ backgroundColor: "#3b82f6" }}>
          {hub} - {hubInfo.name}
        </Badge>
        <h2 className="text-2xl font-bold mb-2" style={{ color: "#fff" }}>
          Draw Your Position
        </h2>
        <p style={{ color: "#9ca3af" }}>
          {playerPosition === null
            ? "Click a card to reveal your bidding position"
            : `You drew position ${playerPosition}! (${POSITION_MULTIPLIERS[playerPosition]}x multiplier)`
          }
        </p>
      </div>

      <div className="flex justify-center gap-2 flex-wrap py-6">
        {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((index) => {
          const isRevealed = cardsRevealed.includes(index);
          let cardPosition = null;
          let isPlayerCard = false;

          if (isRevealed && participants.length > 0) {
            const participant = participants[index];
            cardPosition = participant?.position;
            isPlayerCard = participant?.isPlayer;
          }

          return (
            <PositionCard
              key={index}
              position={cardPosition || index + 1}
              isRevealed={isRevealed}
              isPlayerCard={isPlayerCard}
              onClick={() => handleCardPick(index)}
              disabled={playerPosition !== null && !isRevealed}
            />
          );
        })}
      </div>

      {playerPosition !== null && (
        <div className="p-4 rounded-lg text-center" style={{ backgroundColor: "#052e16", border: "1px solid #22c55e40" }}>
          <p style={{ color: "#86efac" }}>
            Position {playerPosition} = <span style={{ color: "#fbbf24" }}>{POSITION_MULTIPLIERS[playerPosition]}x</span> multiplier on your bid
            <br />
            <span style={{ color: "#9ca3af" }}>
              {playerPosition <= 3
                ? "Great draw! You bid early but get a big bonus."
                : playerPosition >= 8
                ? "You'll see most bids before yours, but smaller bonus."
                : "Mid-position - balanced trade-off."
              }
            </span>
          </p>
        </div>
      )}
    </div>
  );

  // Render bidding phase
  const renderBidding = () => {
    const playerParticipant = participants.find(p => p.isPlayer);
    const isPlayerTurn = playerParticipant?.position === currentBidder && !playerHasBid;
    const previousBids = participants.filter(p => p.bid !== null);

    return (
      <div className="space-y-4">
        <div className="text-center">
          <Badge className="mb-2" style={{ backgroundColor: "#3b82f6" }}>
            {hub} - {hubInfo.name}
          </Badge>
          <h2 className="text-2xl font-bold mb-1" style={{ color: "#fff" }}>
            Sequential Bidding
          </h2>
          {currentHighestBid > 0 && (
            <p style={{ color: "#22c55e" }}>
              Current highest: {formatMoney(currentHighestBid)} effective ({currentHighestBidder?.name})
            </p>
          )}
        </div>

        {/* Bid list */}
        <div className="space-y-2">
          {participants.map((participant, idx) => (
            <BidRow
              key={participant.id}
              participant={participant}
              isPlayer={participant.isPlayer}
              isCurrentHighest={participant.effectiveBid === currentHighestBid && currentHighestBid > 0}
              showBid={participant.bid !== null}
              isWaiting={participant.position === currentBidder && participant.bid === null}
            />
          ))}
        </div>

        {/* Player bid input */}
        {isPlayerTurn && (
          <div className="p-4 rounded-xl" style={{ backgroundColor: "#0c1929", border: "2px solid #3b82f6" }}>
            <div className="text-center mb-4">
              <span className="text-lg font-bold" style={{ color: "#60a5fa" }}>Your Turn!</span>
              <span className="ml-2 text-sm" style={{ color: "#9ca3af" }}>
                (Position {playerPosition} = {POSITION_MULTIPLIERS[playerPosition]}x)
              </span>
            </div>

            {previousBids.length > 0 && currentHighestBid > 0 && (
              <div className="mb-4 p-3 rounded-lg" style={{ backgroundColor: "#1a1a1a" }}>
                <p className="text-sm" style={{ color: "#9ca3af" }}>
                  To beat the current highest ({formatMoney(currentHighestBid)} effective),
                  you need to bid at least: <span style={{ color: "#fbbf24", fontWeight: "bold" }}>
                    {formatMoney(Math.ceil(currentHighestBid / POSITION_MULTIPLIERS[playerPosition]) + 100000)}
                  </span>
                </p>
              </div>
            )}

            <div className="flex gap-3 items-end">
              <div className="flex-1">
                <label className="block text-sm mb-1" style={{ color: "#9ca3af" }}>Your Bid:</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xl font-bold" style={{ color: "#22c55e" }}>$</span>
                  <input
                    type="text"
                    value={playerBid ? parseInt(playerBid.replace(/,/g, "")).toLocaleString() : ""}
                    onChange={(e) => {
                      setPlayerBid(e.target.value.replace(/[^0-9]/g, ""));
                      setBidError("");
                    }}
                    placeholder="0"
                    className="w-full pl-8 pr-4 py-3 text-xl font-bold rounded-lg"
                    style={{ backgroundColor: "#0f0f0f", border: bidError ? "2px solid #ef4444" : "2px solid #333", color: "#fff" }}
                  />
                </div>
                {bidError && <p className="text-sm mt-1" style={{ color: "#ef4444" }}>{bidError}</p>}
                <p className="text-xs mt-1" style={{ color: "#6b7280" }}>
                  Cash: {formatMoney(playerCash)}
                </p>
              </div>
              <div className="text-center pb-1">
                <div className="text-xs mb-1" style={{ color: "#9ca3af" }}>Effective:</div>
                <div className="font-bold" style={{ color: "#22c55e" }}>
                  {formatMoney(Math.round((parseInt(playerBid.replace(/,/g, "")) || 0) * POSITION_MULTIPLIERS[playerPosition]))}
                </div>
              </div>
              <Button
                onClick={handlePlayerBid}
                className="px-6 py-3 font-bold"
                style={{ backgroundColor: "#166534", color: "#fff" }}
              >
                BID
              </Button>
              <Button
                onClick={() => {
                  setPlayerBid("0");
                  setTimeout(handlePlayerBid, 100);
                }}
                className="px-4 py-3"
                style={{ backgroundColor: "#1f2937", color: "#9ca3af", border: "1px solid #374151" }}
              >
                PASS
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render result
  const renderResult = () => {
    const playerWon = winner?.isPlayer;
    const playerBidAmount = participants.find(p => p.isPlayer)?.bid || 0;

    return (
      <div className="space-y-6 text-center">
        <Badge className="mb-2" style={{ backgroundColor: "#3b82f6" }}>
          {hub} - {hubInfo.name}
        </Badge>

        {playerWon ? (
          <div className="p-8 rounded-xl" style={{ backgroundColor: "#052e16", border: "2px solid #22c55e" }}>
            <div className="text-5xl mb-4 font-bold" style={{ color: "#22c55e" }}>WIN</div>
            <h2 className="text-3xl font-bold mb-2" style={{ color: "#22c55e" }}>HUB ACQUIRED</h2>
            <p className="text-xl mb-4" style={{ color: "#86efac" }}>{hubInfo.name} is yours!</p>
            <div className="space-y-1 text-sm" style={{ color: "#9ca3af" }}>
              <p>Your Bid: {formatMoney(playerBidAmount)} × {POSITION_MULTIPLIERS[playerPosition]} = <span style={{ color: "#22c55e" }}>{formatMoney(winner.effectiveBid)}</span> effective</p>
              <p>New Balance: <span style={{ color: "#60a5fa" }}>{formatMoney(playerCash - playerBidAmount)}</span></p>
            </div>
          </div>
        ) : winner?.id === "none" ? (
          <div className="p-8 rounded-xl" style={{ backgroundColor: "#1a1a1a", border: "2px solid #6b7280" }}>
            <div className="text-5xl mb-4">🤷</div>
            <h2 className="text-3xl font-bold mb-2" style={{ color: "#9ca3af" }}>NO WINNER</h2>
            <p className="text-xl" style={{ color: "#6b7280" }}>Everyone passed. The hub remains unawarded.</p>
          </div>
        ) : (
          <div className="p-8 rounded-xl" style={{ backgroundColor: "#1f0a0a", border: "2px solid #ef4444" }}>
            <div className="text-5xl mb-4" style={{ color: "#ef4444" }}>✗</div>
            <h2 className="text-3xl font-bold mb-2" style={{ color: "#ef4444" }}>OUTBID</h2>
            <p className="text-xl mb-4" style={{ color: "#fca5a5" }}>{winner?.name} wins!</p>
            <div className="space-y-1 text-sm" style={{ color: "#9ca3af" }}>
              <p>Your Bid: {formatMoney(playerBidAmount)} × {POSITION_MULTIPLIERS[playerPosition]} = {formatMoney(Math.round(playerBidAmount * POSITION_MULTIPLIERS[playerPosition]))} effective</p>
              <p>Winning Bid: {formatMoney(winner?.bid || 0)} × {POSITION_MULTIPLIERS[winner?.position]} = <span style={{ color: "#22c55e" }}>{formatMoney(winner?.effectiveBid || 0)}</span> effective</p>
            </div>
          </div>
        )}

        {/* Final standings */}
        <div className="p-4 rounded-lg" style={{ backgroundColor: "#1a1a1a", border: "1px solid #333" }}>
          <h3 className="font-bold mb-3" style={{ color: "#9ca3af" }}>Final Standings</h3>
          <div className="space-y-1 text-sm max-h-40 overflow-y-auto">
            {[...participants]
              .sort((a, b) => (b.effectiveBid || 0) - (a.effectiveBid || 0))
              .map((p, i) => (
                <div key={p.id} className="flex justify-between" style={{ color: p.isPlayer ? "#60a5fa" : "#6b7280" }}>
                  <span>{i + 1}. {p.name} (Pos {p.position})</span>
                  <span>{p.bid === 0 ? "PASS" : `${formatMoney(p.bid)} → ${formatMoney(p.effectiveBid)}`}</span>
                </div>
              ))}
          </div>
        </div>

        <Button
          onClick={handleComplete}
          className="px-8 py-4 text-lg font-bold"
          style={{ backgroundColor: playerWon ? "#166534" : "#1e40af", color: "#fff" }}
        >
          {playerWon ? "Claim Your Hub" : "Close"}
        </Button>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: "rgba(0, 0, 0, 0.9)" }}>
      <Card className="w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto" style={{ backgroundColor: "#0a0a0a", border: "2px solid #333" }}>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle style={{ color: "#fff" }}>Hub Auction</CardTitle>
          {phase === "intro" && (
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-800 text-xl" style={{ color: "#9ca3af" }}>X</button>
          )}
        </CardHeader>
        <CardContent className="pb-8">
          {phase === "intro" && renderIntro()}
          {phase === "draw" && renderDraw()}
          {phase === "bidding" && renderBidding()}
          {phase === "result" && renderResult()}
        </CardContent>
      </Card>
    </div>
  );
}
